import {
  formatNumber,
  roundToNearestIncrement,
} from '../../../../helpers/formatting';
import { findAttributeValues } from '../../../../helpers/findAttributeValues';
import { Currency } from '../../../../types';
import type { SectionView, IAdAttributeView } from '../../../../types';
import type {
  FinanceInfo,
  FinanceLink,
  DealerSummary,
} from '../../../../components/PageWrappers/AdDetails/types';
import {
  appendFinanceTrackingParams,
  type FinanceTrackingParams,
} from '../components/AdTitleBox/financeTracking';

import { truncateSearchTerms } from '../../../../helpers/formatSearchTerms';
import getConfig from 'next/config';

export const generateSaveSearchModalData = (
  displayAttributes: IAdAttributeView[],
  price: string | null,
  currency: Currency,
  section?: SectionView,
  county?: string,
  wanted?: boolean,
) => {
  const { make, model, year, transmission, bodyType } = findAttributeValues(
    displayAttributes,
    ['make', 'model', 'year', 'transmission', 'bodyType'],
  );
  const priceAsNumber = price ? parseInt(price.replace(/,/g, '')) : 0;
  const chosenCurrency = currency === 'GBP' ? '£' : '€';

  const name = `${make ?? 'unknown make'} ${model ?? 'unknown model'} in ${
    section?.displayName ?? 'unknown section'
  }`;
  const formattedPrice =
    priceAsNumber > 1
      ? `${chosenCurrency}${formatNumber(
          roundToNearestIncrement(priceAsNumber * 0.85, 100),
        )} - ${chosenCurrency}${formatNumber(
          roundToNearestIncrement(priceAsNumber * 1.15, 100),
        )}`
      : undefined;
  const formattedYear = year
    ? `${parseInt(year) - 1} - ${parseInt(year) + 1}`
    : undefined;
  const formattedCounty = county
    ? `${county.replace('Co. ', '')} (All Areas)`
    : undefined;
  const formattedAdType = wanted ? 'Wanted' : undefined;
  const formattedMakeModel = make && model ? `${make} ${model}` : undefined;
  const vehicleAttributes = [
    section?.displayName,
    formattedMakeModel,
    formattedYear,
    formattedPrice,
    formattedAdType,
    formattedCounty,
    transmission,
    bodyType,
  ];
  const terms = vehicleAttributes.filter(
    (attribute): attribute is string => !!attribute,
  );
  const truncatedTerms = truncateSearchTerms(terms, 5);

  return {
    name,
    terms: truncatedTerms,
  };
};

interface FinanceInfoLink
  extends Omit<FinanceInfo, 'partnerName' | 'partnerType'> {}

export const generateFinanceLink = (financeInfo: FinanceInfoLink) =>
  financeInfo?.internalRedirectURL || financeInfo?.externalRedirectURL || '';

const isFirstPartyUrl = (url: string) =>
  url.startsWith('/') || url.startsWith('https://www.donedeal.ie');

const paramsForUrl = (url: string, params: FinanceTrackingParams) =>
  isFirstPartyUrl(url) ? params : { ...params, userId: undefined };

export const enrichFinanceInfo = <T extends FinanceInfo | null | undefined>(
  financeInfo: T,
  params: FinanceTrackingParams,
): T => {
  if (!financeInfo) return financeInfo;
  return {
    ...financeInfo,
    ...(financeInfo.internalRedirectURL && {
      internalRedirectURL: appendFinanceTrackingParams(
        financeInfo.internalRedirectURL,
        paramsForUrl(financeInfo.internalRedirectURL, params),
      ),
    }),
    ...(financeInfo.externalRedirectURL && {
      externalRedirectURL: appendFinanceTrackingParams(
        financeInfo.externalRedirectURL,
        paramsForUrl(financeInfo.externalRedirectURL, params),
      ),
    }),
  };
};

export const enrichFinanceLink = <T extends FinanceLink | undefined>(
  financeLink: T,
  params: FinanceTrackingParams,
): T => {
  if (!financeLink?.url) return financeLink;
  return {
    ...financeLink,
    url: appendFinanceTrackingParams(
      financeLink.url,
      paramsForUrl(financeLink.url, params),
    ),
  };
};

export const getMileageValueFromString = (mileage: string) =>
  mileage.replace(/\D/g, '');
export const getMileageUnitFromString = (mileage: string) =>
  mileage.includes('mi') ? 'MILES' : 'KILOMETERS';

export const getSellerPhoto = (
  isDealer: boolean,
  dealer?: DealerSummary,
): string => {
  const {
    publicRuntimeConfig: { CDN_STATIC_ASSETS },
  } = getConfig();

  if (isDealer && dealer?.logo?.small) {
    return dealer.logo.small;
  }

  return `${CDN_STATIC_ASSETS}/images/illustrations/profile-new.svg`;
};
